import React, { FC } from 'react';
import { Box, Switch, Flex, RadioGroup, useRadioGroupContext } from '@chakra-ui/react';
import { Props } from './type';
import { Checkbox } from '../Checkbox';
import { Radio } from '../Radio';

// RadioGroup.Item needs a RadioGroup.Root, and strict useRadioGroupContext signals a missing one only by throwing.
// Lone radios (e.g. OrgWizardUserTypes) have none; the hook's call position never varies, so hook rules hold.
const useHasAmbientRadioGroup = (): boolean => {
  try {
    useRadioGroupContext();
    return true;
  } catch {
    return false;
  }
};

export const SelectionControl: FC<Props> = props => {
  const {
    children,
    isDisabled,
    disabled,
    hideLabel,
    name,
    inputRef,
    id,
    key,
    onChange,
    onClick,
    labelAction,
    padding: userPadding = {},
    isChecked,
    checked,
    isIndeterminate,
    indeterminate,
    isReadOnly,
    readOnly,
    stacked,
    type,
    value,
    toggleLabelSide,
    label,
  } = props;

  const padding = {
    ...{ pt: 0, pr: 4, pb: 0, pl: 0 },
    ...userPadding,
  };

  let currentType = type;
  if (type === 'checkbox' || type === 'option') {
    currentType = 'checkbox';
  } else if (type === 'switch' || type === 'toggle') {
    currentType = 'toggle';
  }

  // Called unconditionally regardless of currentType so hook call order stays stable.
  const hasAmbientRadioGroup = useHasAmbientRadioGroup();

  const filteredDataAttibsInputProps = (): { [key: string]: any } => {
    // This will be props for the input of the Chakra component (inputProps).
    // Filter out anything other than data attributes to avoid errors.
    const result: { [key: string]: any } = {};
    for (const key in props) {
      if (key.startsWith('data-')) {
        result[key] = props[key as keyof typeof props];
      }
    }
    /**
     * The currentType will, in the end, only be checkbox, radio, or toggle
     * to keep it predictible for testing suits
     */
    result['data-component-type'] = currentType;
    return result;
  };

  // Accepts both the legacy (isChecked/isDisabled/...) and native (checked/disabled/...) prop
  // names on this component's own boundary; the legacy name wins if both are passed.
  const isItChecked = isChecked ?? checked ?? false;

  const commonProps = {
    // `disabled`, not `isDisabled`: the toggle branch renders the raw `Switch.Root` (not the dual-name Toggle
    // wrapper), which only reads `disabled`; Checkbox and Radio accept either name.
    disabled: isDisabled ?? disabled,
    name,
    id,
    key,
    onChange,
    // Checkbox.Root/Switch.Root are controlled via `checked` and ignore `isChecked`, so without this the control
    // is uncontrolled and the parent's stored value never drives it.
    checked: isItChecked,
    'data-action': labelAction,
    value,
    ...filteredDataAttibsInputProps(),
  };

  // RadioGroup.Item has no `indeterminate`/`readOnly` (only value/disabled/invalid), so these stay on the
  // checkbox/toggle branches instead of commonProps, keeping an unrecognized attribute off Radio.Item.
  const readOnlyProp = { readOnly: isReadOnly ?? readOnly };
  const checkboxOnlyProps = { indeterminate: isIndeterminate ?? indeterminate, ...readOnlyProp };

  // Checkbox/Radio/Switch each forward this ref to a different root element
  // (label/div/label respectively), so it's cast per-target rather than shared.
  const rootRef = inputRef as never;

  const renderElement = () => {
    if (currentType === 'checkbox') {
      return (
        <Box {...padding}>
          <Flex align="start">
            <Box alignItems={stacked ? 'center' : 'left'}>
              <Checkbox
                label={label}
                gap="2"
                {...commonProps}
                {...checkboxOnlyProps}
                ref={rootRef}
                variant={(stacked ? 'stacked' : undefined) as never}
              >
                {children}
              </Checkbox>
            </Box>
          </Flex>
        </Box>
      );
    } else if (currentType === 'radio') {
      // No explicit `role="radio"`: Radio.Item's <input type="radio"> carries it implicitly, and a second one on
      // the wrapping <label> makes two elements match every role="radio" query.
      const radioItem = (
        <Radio label={label} gap="2" {...commonProps} value={value ?? ''} ref={rootRef} onClick={onClick}>
          {children}
        </Radio>
      );
      return (
        <Flex align={stacked ? 'center' : 'start'} direction={stacked ? 'column' : 'row'}>
          {/* Grouped callers (RadioGroupControl) bring their own RadioGroup.Root; a lone radio (CheckboxControl,
              MaterialCheckbox) has none, so one is self-provided, since Radio.Item always needs a Root. */}
          {hasAmbientRadioGroup ? (
            radioItem
          ) : (
            // RadioGroup.Item has no checked prop; checked state comes only from this self-provided Root's
            // `value` matching the item's own `value`.
            <RadioGroup.Root name={name} value={isItChecked ? (value ?? '') : null}>
              {radioItem}
            </RadioGroup.Root>
          )}
        </Flex>
      );
    } else if (currentType === 'toggle') {
      let labelSide = children && !toggleLabelSide ? 'right' : toggleLabelSide;
      labelSide = !hideLabel && React.Children.count(children) > 0 ? labelSide : '';

      return (
        <Box display="flex" alignItems="center" {...padding}>
          <Flex align={stacked ? 'center' : 'start'} direction={stacked ? 'column' : 'row'}>
            {labelSide === 'left' && (
              <Box mr="2" {...filteredDataAttibsInputProps()}>
                {children}
              </Box>
            )}
            <Switch.Root {...commonProps} {...readOnlyProp} ref={rootRef} onChange={onChange as never}>
              <Switch.HiddenInput />
              <Switch.Control />
            </Switch.Root>
            {labelSide === 'right' && (
              <Box ml="2" mt={stacked ? 2 : 0} {...filteredDataAttibsInputProps()}>
                {children}
              </Box>
            )}
          </Flex>
        </Box>
      );
    }
  };

  return <>{renderElement()}</>;
};
